/**
 * Validador manual de contracts/input.schema.json.
 *
 * Deliberadamente NO usa ajv en runtime: ajv.compile() genera código con
 * `new Function(...)`, incompatible con una CSP estricta sin 'unsafe-eval'.
 * Este validador se ejercita en tests/contract contra ajv (solo en Node/test)
 * para garantizar que no diverge del JSON Schema real.
 */
import type { EngineError, EngineInput } from "./types";

const SCENARIO_ID_PATTERN = /^[a-z0-9-]+$/;
const MAX_SCENARIO_ID_LENGTH = 80;
const MAX_PAYLOAD_PROPERTIES = 200;

export interface ValidationResult {
  valid: boolean;
  errors: EngineError[];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Valida `raw` contra contracts/input.schema.json. No lanza excepciones:
 * siempre devuelve una lista de errores tipados con `path` en formato JSON
 * Pointer para que la UI pueda señalar el campo exacto.
 */
export function validateEngineInput(raw: unknown): ValidationResult {
  const errors: EngineError[] = [];

  if (!isPlainObject(raw)) {
    return {
      valid: false,
      errors: [{ code: "INPUT_INVALID", message: "La entrada debe ser un objeto JSON.", path: "#" }],
    };
  }

  const allowedTopLevel = new Set(["schemaVersion", "scenarioId", "payload", "options"]);
  for (const key of Object.keys(raw)) {
    if (!allowedTopLevel.has(key)) {
      errors.push({
        code: "INPUT_INVALID",
        message: `Propiedad no permitida en la entrada: "${key}".`,
        path: `#/${key}`,
      });
    }
  }

  for (const required of ["schemaVersion", "scenarioId", "payload", "options"]) {
    if (!(required in raw)) {
      errors.push({
        code: "INPUT_INVALID",
        message: `Falta la propiedad requerida "${required}".`,
        path: `#/${required}`,
      });
    }
  }

  if ("schemaVersion" in raw && raw.schemaVersion !== "1.0.0") {
    errors.push({
      code: "INPUT_INVALID",
      message: 'schemaVersion debe ser exactamente "1.0.0".',
      path: "#/schemaVersion",
    });
  }

  if ("scenarioId" in raw) {
    const scenarioId = raw.scenarioId;
    if (typeof scenarioId !== "string") {
      errors.push({ code: "INPUT_INVALID", message: "scenarioId debe ser texto.", path: "#/scenarioId" });
    } else {
      if (scenarioId.length < 1 || scenarioId.length > MAX_SCENARIO_ID_LENGTH) {
        errors.push({
          code: "INPUT_INVALID",
          message: `scenarioId debe tener entre 1 y ${MAX_SCENARIO_ID_LENGTH} caracteres.`,
          path: "#/scenarioId",
        });
      }
      if (!SCENARIO_ID_PATTERN.test(scenarioId)) {
        errors.push({
          code: "INPUT_INVALID",
          message: "scenarioId solo admite minúsculas, números y guiones.",
          path: "#/scenarioId",
        });
      }
    }
  }

  if ("payload" in raw) {
    const payload = raw.payload;
    if (!isPlainObject(payload)) {
      errors.push({ code: "INPUT_INVALID", message: "payload debe ser un objeto JSON.", path: "#/payload" });
    } else if (Object.keys(payload).length > MAX_PAYLOAD_PROPERTIES) {
      errors.push({
        code: "LIMIT_EXCEEDED",
        message: `payload no puede superar ${MAX_PAYLOAD_PROPERTIES} propiedades de primer nivel.`,
        path: "#/payload",
      });
    }
  }

  if ("options" in raw) {
    const options = raw.options;
    if (!isPlainObject(options)) {
      errors.push({ code: "INPUT_INVALID", message: "options debe ser un objeto JSON.", path: "#/options" });
    } else {
      const allowedOptionKeys = new Set(["deterministic"]);
      for (const key of Object.keys(options)) {
        if (!allowedOptionKeys.has(key)) {
          errors.push({
            code: "INPUT_INVALID",
            message: `Propiedad no permitida en options: "${key}".`,
            path: `#/options/${key}`,
          });
        }
      }
      if (!("deterministic" in options)) {
        errors.push({
          code: "INPUT_INVALID",
          message: 'Falta la propiedad requerida "deterministic" en options.',
          path: "#/options/deterministic",
        });
      } else if (typeof options.deterministic !== "boolean") {
        errors.push({
          code: "INPUT_INVALID",
          message: "options.deterministic debe ser booleano.",
          path: "#/options/deterministic",
        });
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

export function assertEngineInput(raw: unknown): EngineInput {
  const result = validateEngineInput(raw);
  if (!result.valid) {
    const message = result.errors.map((error) => error.message).join(" ");
    throw new Error(message);
  }
  return raw as EngineInput;
}

const VALID_SEVERITIES = new Set(["info", "warning", "error", "critical"]);
const VALID_STATUSES = new Set(["completed", "partial", "failed", "cancelled"]);

/**
 * Valida manualmente contracts/output.schema.json. Se usa en tests de contrato
 * y como comprobación defensiva antes de exponer un EngineOutput a la UI o a
 * la exportación, para garantizar que el motor nunca emite una envolvente
 * incompatible con el contrato publicado.
 */
export function validateEngineOutput(raw: unknown): ValidationResult {
  const errors: EngineError[] = [];
  if (!isPlainObject(raw)) {
    return { valid: false, errors: [{ code: "INTERNAL_ERROR", message: "La salida debe ser un objeto.", path: "#" }] };
  }

  if (raw.schemaVersion !== "1.0.0") {
    errors.push({ code: "INTERNAL_ERROR", message: 'schemaVersion debe ser "1.0.0".', path: "#/schemaVersion" });
  }
  if (typeof raw.runId !== "string" || raw.runId.length < 1 || raw.runId.length > 100) {
    errors.push({ code: "INTERNAL_ERROR", message: "runId inválido.", path: "#/runId" });
  }
  if (typeof raw.status !== "string" || !VALID_STATUSES.has(raw.status)) {
    errors.push({ code: "INTERNAL_ERROR", message: "status inválido.", path: "#/status" });
  }
  if (typeof raw.summary !== "string" || raw.summary.length < 1 || raw.summary.length > 500) {
    errors.push({ code: "INTERNAL_ERROR", message: "summary inválido.", path: "#/summary" });
  }
  if (!Array.isArray(raw.findings) || raw.findings.length > 1000) {
    errors.push({ code: "INTERNAL_ERROR", message: "findings inválido o excede el máximo de 1000.", path: "#/findings" });
  } else {
    raw.findings.forEach((finding, index) => {
      if (!isPlainObject(finding)) {
        errors.push({ code: "INTERNAL_ERROR", message: "Cada finding debe ser un objeto.", path: `#/findings/${index}` });
        return;
      }
      if (typeof finding.ruleId !== "string") {
        errors.push({ code: "INTERNAL_ERROR", message: "ruleId inválido.", path: `#/findings/${index}/ruleId` });
      }
      if (typeof finding.severity !== "string" || !VALID_SEVERITIES.has(finding.severity)) {
        errors.push({ code: "INTERNAL_ERROR", message: "severity inválida.", path: `#/findings/${index}/severity` });
      }
      if (typeof finding.message !== "string" || finding.message.length > 1000) {
        errors.push({ code: "INTERNAL_ERROR", message: "message inválido.", path: `#/findings/${index}/message` });
      }
    });
  }
  if (!isPlainObject(raw.evidence)) {
    errors.push({ code: "INTERNAL_ERROR", message: "evidence inválido.", path: "#/evidence" });
  } else {
    if (typeof raw.evidence.rulesVersion !== "string") {
      errors.push({ code: "INTERNAL_ERROR", message: "evidence.rulesVersion inválido.", path: "#/evidence/rulesVersion" });
    }
    if (typeof raw.evidence.scenarioId !== "string") {
      errors.push({ code: "INTERNAL_ERROR", message: "evidence.scenarioId inválido.", path: "#/evidence/scenarioId" });
    }
  }

  return { valid: errors.length === 0, errors };
}
